import { Simulation } from '../types';

/**
 * How long after the last transcript write a 'generating' row is still treated as actively
 * generating. Beyond this, we assume the generating session died (e.g. the tab was closed)
 * and allow evaluation so the row is not stuck as un-evaluatable forever.
 *
 * Must exceed the slowest expected gap between turns; tune against production turn latency.
 */
export const SIMULATION_GENERATING_STALE_MS = 3 * 60 * 1000;

/**
 * Whether a simulation is still actively being generated, and therefore must not be
 * evaluated. A 'generating' row whose last write is older than
 * SIMULATION_GENERATING_STALE_MS is treated as abandoned and becomes evaluatable again.
 */
export function isSimulationGenerating(simulation: Simulation): boolean {
  if (simulation.status !== 'generating') return false;
  const lastUpdate = new Date(simulation.updated_at).getTime();
  return Date.now() - lastUpdate < SIMULATION_GENERATING_STALE_MS;
}

/**
 * A simulation that stopped before reaching its turns. Its transcript is a
 * half conversation, so scoring it would give a plausible but wrong result.
 */
export function isSimulationFailed(simulation: Simulation): boolean {
  return simulation.status === 'failed';
}

/** Whether evaluation must wait (still running) or is refused (stopped early). */
export function isEvaluationBlocked(simulation: Simulation): boolean {
  return isSimulationGenerating(simulation) || isSimulationFailed(simulation);
}

/** "Stopped at turn 3 of 5: <why>", stored on a failed simulation. */
export function describeEarlyStop(
  turn: number,
  totalTurns: number,
  error: unknown,
): string {
  const why = error instanceof Error ? error.message : 'Unknown error';
  return `Stopped at turn ${turn} of ${totalTurns}: ${why}`;
}
